import type { ShopContentLanguage } from './i18n';
import type { Language } from '../types';
import { _t } from './i18n';
import {
  copyKeyAliases,
  groupLabelKeys,
  guestProfileLabelKey,
  memberProfileLabelKey,
  moduleLabelKeys,
  visibleCatalogCardFieldKeys,
} from '@brand/terminology';
import type { CatalogCardFieldKey, CatalogCardFields } from '../types';
import { PRODUCT_FEATURES } from '../constants/productSurface';
import { TENANT_MODULE_DEFINITIONS } from '../constants/tenantModuleGroups';
import { brand } from './brand';

/** Tenant admin / preview toolbar module label (e.g. products → items). */
export function resolveTenantModuleLabelKey(moduleKey: string): string {
  return (
    moduleLabelKeys[moduleKey]
    ?? TENANT_MODULE_DEFINITIONS[moduleKey]?.labelKey
    ?? moduleKey
  );
}

/** Slot group label in tenant toolbar dropdown. */
export function resolveTenantGroupLabelKey(groupId: string, defaultLabelKey: string): string {
  return groupLabelKeys[groupId] ?? defaultLabelKey;
}

/** Map shared i18n keys to brand copy (products.* → items.* on Osfim). */
export function resolveBrandCopyKey(key: string): string {
  return copyKeyAliases[key] ?? key;
}

/** Brand-aware i18n — applies copyKeyAliases then translates. */
export function brandT(key: string, lang: ShopContentLanguage): string {
  return _t(resolveBrandCopyKey(key), lang);
}

/** Catalog card field toggles for Settings → Design (brand-specific). */
export function resolveVisibleCatalogCardFieldKeys(): readonly CatalogCardFieldKey[] {
  return visibleCatalogCardFieldKeys;
}

/** Hide disabled card fields on save and in storefront settings payload. */
export function clampCatalogCardFieldsForBrand(fields: CatalogCardFields): CatalogCardFields {
  const allowed = new Set(visibleCatalogCardFieldKeys);
  return {
    name: allowed.has('name') ? fields.name : false,
    sku: allowed.has('sku') ? fields.sku : false,
    price: allowed.has('price') ? fields.price : false,
    stock: allowed.has('stock') ? fields.stock : false,
  };
}

/** Tenant landing CTA — brand copy only (not platform landing-copy.json). */
export function landingOpenCatalogLabel(lang: Language): string {
  return brandT('landing.openCatalog', lang);
}

export function resolveStorefrontProfileLabelKey(isGuest: boolean): string {
  // No client registration yet → profile tab is «Обо мне», not «Регистрация».
  if (brand.capabilities.biography && !PRODUCT_FEATURES.registeredClients) {
    return 'nav.aboutMe';
  }
  return isGuest ? guestProfileLabelKey : memberProfileLabelKey;
}
